import React, { useState, useEffect } from 'react';
import { X, Zap, ArrowRight, Sparkles, Infinity as InfinityIcon, Heart, Gift, Percent, Star } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';

interface OfferPopupProps {
  onSelectPlan: (planName: string) => void;
  location?: 'landing' | 'dashboard';
}

interface Particle {
  id: number;
  x: number;
  size: number;
  delay: number;
  duration: number;
  char: string;
  colorClass: string;
}

interface DBOffer {
  id: string;
  popup_id: string;
  title: string;
  badge: string;
  description: string;
  price: string;
  old_price: string;
  discount: string;
  action_text: string;
  action_link: string;
  color: string;
  icon: string;
  delay_seconds: number;
  duration_seconds: number;
  location: string;
  position: string;
  is_active: boolean;
}

export default function OfferPopup({ onSelectPlan, location = 'landing' }: OfferPopupProps) {
  const [offers, setOffers] = useState<DBOffer[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      const dismissedTimeStr = localStorage.getItem(`si_offer_dismissed_time_${location}`);
      if (dismissedTimeStr) {
        const dismissedTime = parseInt(dismissedTimeStr, 10);
        if (!isNaN(dismissedTime) && Date.now() - dismissedTime < 24 * 60 * 60 * 1000) {
          return true;
        }
      }
      return false;
    } catch {
      return false;
    }
  });

  // Fetch dynamic popups from Supabase
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const { data, error } = await supabase
          .from('promo_popups')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (!error && data && data.length > 0) {
          // Filter popups based on location match (landing, dashboard, or both)
          const filtered = data.filter((offer: DBOffer) => {
            const loc = offer.location || 'both';
            return loc === 'both' || loc === location;
          });
          setOffers(filtered);
        } else {
          // Fallback static premium offers in case DB has none
          setOffers([
            {
              id: 'lifetime',
              popup_id: 'lifetime',
              title: 'Lifetime License',
              badge: 'Flash Offer',
              description: 'Get permanent premium access with all features and future updates for a one-time low cost.',
              price: '$49',
              old_price: '$199',
              discount: '75% OFF',
              icon: 'Zap',
              action_text: 'Claim Lifetime Access Now',
              action_link: 'https://smartimagify.com/pricing',
              color: '#aace16',
              delay_seconds: 10,
              duration_seconds: 32,
              location: 'both',
              position: 'bottom-right',
              is_active: true
            },
            {
              id: 'premium',
              popup_id: 'premium',
              title: 'Unlimited AI Premium',
              badge: 'Pro Feature',
              description: 'Generate photorealistic and creative artwork directly in your browser with zero limits.',
              price: '$3',
              old_price: '$5',
              discount: '40% OFF',
              icon: 'Sparkles',
              action_text: 'Upgrade to Premium Now',
              action_link: 'https://smartimagify.com/pricing',
              color: '#38bdf8',
              delay_seconds: 8,
              duration_seconds: 30,
              location: 'both',
              position: 'bottom-right',
              is_active: true
            },
            {
              id: 'ultra',
              popup_id: 'ultra',
              title: 'Enterprise Ultra Power',
              badge: 'Unlimited Batch',
              description: 'Process thousands of files simultaneously with maximum CPU multi-threading and no caps.',
              price: '$7',
              old_price: '$12',
              discount: '40% OFF',
              icon: 'Infinity',
              action_text: 'Get Ultra Power Now',
              action_link: 'https://smartimagify.com/pricing',
              color: '#a855f7',
              delay_seconds: 15,
              duration_seconds: 35,
              location: 'both',
              position: 'bottom-right',
              is_active: true
            },
            {
              id: 'donation',
              popup_id: 'donation',
              title: 'Support the Needy',
              badge: 'Charity Fund',
              description: '100% of your donations go directly to providing essential support for the poor and needy.',
              price: 'Donate',
              old_price: '',
              discount: 'Any Amount',
              icon: 'Heart',
              action_text: 'Donate & Support',
              action_link: 'https://smartimagify.com/pricing',
              color: '#ef4444',
              delay_seconds: 12,
              duration_seconds: 32,
              location: 'both',
              position: 'bottom-right',
              is_active: true
            }
          ]);
        }
      } catch (e) {
        console.error("Error fetching promo popups:", e);
      }
    };
    fetchOffers();
  }, [location]);

  // Master timing flow: delay -> show -> duration -> hide -> next slide delay
  useEffect(() => {
    if (isDismissed || offers.length === 0) return;

    const offer = offers[currentSlide];
    const delay = (offer.delay_seconds ?? 5) * 1000;
    const duration = (offer.duration_seconds ?? 30) * 1000;

    let showTimer: ReturnType<typeof setTimeout>;
    let hideTimer: ReturnType<typeof setTimeout>;

    // Step 1: Wait for delay, then set visible
    showTimer = setTimeout(() => {
      setIsVisible(true);

      // Step 2: Set timer to auto-hide after duration
      hideTimer = setTimeout(() => {
        setIsVisible(false);
        // Cycle to next slide and start again
        setTimeout(() => {
          setCurrentSlide(prev => (prev + 1) % offers.length);
        }, 800); // short transition buffer
      }, duration);

    }, delay);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [isDismissed, currentSlide, offers]);

  // Spawn Screen Particles (Only on Landing Page AND only on Donation / Heart icon Slide)
  useEffect(() => {
    if (!isVisible || isDismissed || location !== 'landing' || offers.length === 0) {
      setParticles([]);
      return;
    }

    const offer = offers[currentSlide];
    if (offer.icon !== 'Heart' && !offer.title.toLowerCase().includes('charity')) {
      setParticles([]);
      return;
    }

    const newParticles: Particle[] = Array.from({ length: 22 }).map((_, i) => ({
      id: Math.random() + i,
      x: 3 + Math.random() * 94, // distribute across the entire screen viewport width
      size: 14 + Math.random() * 20,
      delay: Math.random() * 2.2,
      duration: 5.5 + Math.random() * 4,
      char: '❤️',
      colorClass: 'text-red-500'
    }));

    setParticles(newParticles);
  }, [currentSlide, isVisible, isDismissed, location, offers]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    try {
      localStorage.setItem(`si_offer_dismissed_time_${location}`, Date.now().toString());
    } catch {}
  };

  const handleActionClick = (offer: DBOffer) => {
    const link = offer.action_link || '';
    if (link.startsWith('http')) {
      window.open(link, '_blank');
    } else {
      onSelectPlan(offer.popup_id);
    }
    setIsVisible(false);
    setIsDismissed(true);
    try {
      localStorage.setItem(`si_offer_dismissed_time_${location}`, Date.now().toString());
    } catch {}
  };

  if (!isVisible || isDismissed || offers.length === 0) return null;

  const currentOffer = offers[currentSlide];

  // Helper to dynamically render Icon
  const getIconComponent = (name: string, color: string) => {
    const props = { className: 'w-5 h-5 transition-colors duration-500', style: { color } };
    switch (name) {
      case 'Zap': return <Zap {...props} />;
      case 'Sparkles': return <Sparkles {...props} />;
      case 'Infinity': return <InfinityIcon {...props} />;
      case 'Heart': return <Heart {...props} />;
      case 'Gift': return <Gift {...props} />;
      case 'Percent': return <Percent {...props} />;
      case 'Star': return <Star {...props} />;
      default: return <Sparkles {...props} />;
    }
  };

  // Determine alignment classes based on position settings
  const getPositionClasses = (pos: string) => {
    const base = 'fixed z-[100] w-[calc(100vw-2rem)] sm:w-[340px] transition-all duration-500';
    switch (pos) {
      case 'bottom-left':
        return `${base} bottom-4 left-4 md:bottom-6 md:left-6`;
      case 'bottom-right':
        return `${base} bottom-4 right-4 md:bottom-6 md:right-6`;
      case 'left':
        return `${base} bottom-1/2 translate-y-1/2 left-4 md:left-6`;
      case 'right':
        return `${base} bottom-1/2 translate-y-1/2 right-4 md:right-6`;
      default:
        return `${base} bottom-4 right-4 md:bottom-6 md:right-6`;
    }
  };

  return (
    <>
      {/* FIXED FULL-SCREEN PORTAL FOR HEARTS: Satisfies charity / hearts rise particle effect */}
      {location === 'landing' && (currentOffer.icon === 'Heart' || currentOffer.title.toLowerCase().includes('charity')) && (
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-[90]">
          {particles.map((p) => (
            <span
              key={p.id}
              className={`absolute bottom-0 animate-particle-rise select-none filter drop-shadow-[0_0_8px_rgba(239,68,68,0.2)] ${p.colorClass}`}
              style={{
                left: `${p.x}%`,
                fontSize: `${p.size}px`,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.duration}s`,
              }}
            >
              {p.char}
            </span>
          ))}
        </div>
      )}

      <div className={getPositionClasses(currentOffer.position)}>
        {/* Premium Glass Container */}
        <div className="relative overflow-hidden bg-[var(--bg-2)]/95 backdrop-blur-xl border border-[var(--border)] rounded-[2rem] shadow-[var(--shadow-lg)] p-6 group transition-all duration-500 hover:border-[var(--border)]/80 animate-offer-card-entry">
          
          {/* Scoped CSS Keyframes */}
          <style>{`
            @keyframes offer-card-entry {
              0% { transform: scale(0.92) translateY(20px); opacity: 0; }
              100% { transform: scale(1) translateY(0); opacity: 1; }
            }
            @keyframes particle-rise {
              0% { transform: translateY(105vh) scale(0.55) rotate(0deg); opacity: 0; }
              15% { opacity: 0.75; }
              85% { opacity: 0.6; }
              100% { transform: translateY(-10vh) scale(1.2) rotate(15deg); opacity: 0; }
            }
            @keyframes gradient-rotate {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .animate-offer-card-entry {
              animation: offer-card-entry 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            }
            .animate-particle-rise {
              animation: particle-rise linear infinite;
            }
            .animate-glow-rotate {
              animation: gradient-rotate 12s linear infinite;
            }
          `}</style>

          {/* Ambient Moving Tech Glow Layer */}
          <div 
            className="absolute -top-12 -right-12 w-36 h-36 rounded-full blur-[45px] pointer-events-none transition-colors duration-1000 z-0 opacity-25 animate-glow-rotate"
            style={{ backgroundColor: currentOffer.color }}
          />

          {/* Tech Grid Background (Clean & Minimal) */}
          <div className="absolute inset-0 bg-[linear-gradient(var(--border)_1px,transparent_1px),linear-gradient(90deg,var(--border)_1px,transparent_1px)] bg-[size:20px_20px] opacity-10 pointer-events-none" />

          {/* Close button with premium look */}
          <button 
            onClick={handleDismiss}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-[var(--text-3)] hover:text-[var(--text-1)] bg-[var(--panel-bg)] hover:bg-[var(--bg-3)] rounded-full transition-all cursor-pointer z-30 border border-[var(--border)]"
            title="Close Offer"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Main Content inside slide keys */}
          <div key={currentSlide} className="relative z-10 space-y-4 animate-in fade-in slide-in-from-right-4 duration-500 cubic-bezier(0.16, 1, 0.3, 1) fill-mode-both">
            
            {/* Header Row */}
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-2xl flex items-center justify-center transition-colors duration-500 shadow-inner border border-[var(--border)]"
                style={{ backgroundColor: `${currentOffer.color}20` }}
              >
                {getIconComponent(currentOffer.icon, currentOffer.color)}
              </div>
              <div>
                <span 
                  className="block text-[9px] font-black uppercase tracking-[0.25em] leading-none mb-1.5 transition-colors duration-500"
                  style={{ color: currentOffer.color }}
                >
                  {currentOffer.badge}
                </span>
                <h3 className="text-base font-black text-[var(--text-1)] leading-none tracking-tight">{currentOffer.title}</h3>
              </div>
            </div>

            {/* Description Block */}
            <div className="space-y-3.5 min-h-[72px]">
              <p className="text-[11.5px] text-[var(--text-2)] leading-relaxed pr-3 font-medium font-sans text-left">
                {currentOffer.description}
              </p>
              
              <div className="flex items-baseline gap-2 pt-0.5">
                <span className={`font-black text-[var(--text-1)] leading-none ${currentOffer.price === 'Donate' ? 'text-2xl' : 'text-3xl'}`}>
                  {currentOffer.price}
                </span>
                {currentOffer.old_price && (
                  <span className="text-sm text-[var(--text-3)] line-through font-bold">{currentOffer.old_price}</span>
                )}
                {currentOffer.discount && (
                  <span 
                    className="text-[9px] font-black px-2 py-0.5 rounded-full transition-colors duration-500 uppercase tracking-wider"
                    style={{ backgroundColor: `${currentOffer.color}20`, color: currentOffer.color }}
                  >
                    {currentOffer.discount}
                  </span>
                )}
              </div>
            </div>

            {/* Action Action Button with glowing border */}
            <button 
              onClick={() => handleActionClick(currentOffer)}
              className="w-full py-3 text-black text-xs font-black rounded-2xl transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 cursor-pointer shadow-lg active:opacity-90"
              style={{ 
                backgroundColor: currentOffer.color, 
                boxShadow: `0 8px 24px ${currentOffer.color}35` 
              }}
            >
              <span>{currentOffer.action_text || 'Claim Offer'}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Minimal Progress Dots */}
          <div className="relative z-10 flex items-center justify-center gap-1.5 mt-4 pt-1">
            {offers.map((_, idx) => (
              <div 
                key={idx} 
                className={`h-1 rounded-full transition-all duration-500 ${
                  idx === currentSlide ? 'w-5 opacity-100' : 'w-1 opacity-35'
                }`}
                style={{ backgroundColor: idx === currentSlide ? currentOffer.color : 'var(--border)' }}
              />
            ))}
          </div>

        </div>
      </div>
    </>
  );
}
